import { FindIcon } from '@/components/IconButton';
import { cn } from '@/utils/classname';
import { search } from '@/utils/search';
import { Input } from '@skand/ui';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useMemo, useRef, useState } from 'react';

export interface ItemProps {
  label: string;
  value: string;
}

export interface InfoBarSelectorProps {
  items: ItemProps[];
  onItemClick: (item: ItemProps) => void;
  enableSearch?: boolean;
  placeholder?: string;
}

export const InfoBarSelector = ({
  items,
  onItemClick,
  enableSearch = false,
  placeholder,
}: InfoBarSelectorProps) => {
  const parentRef = useRef(null);

  const [searchKey, setSearchKey] = useState('');
  const filtered = useMemo(
    () => items.filter(item => search(item.label, searchKey)),
    [items, searchKey],
  );

  // The virtualizer
  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 36,
    overscan: 8,
  });

  return (
    <div className="w-100dvw overflow-hidden p-3 sm:w-auto">
      <div className={cn('bg-neutral-800 bg-opacity-50 rounded-1 backdrop-blur-sm p-2 w-auto')}>
        {enableSearch && (
          <div className="pb-2">
            <Input
              label={placeholder ?? 'Search'}
              onChange={setSearchKey}
              tail={<FindIcon />}
              value={searchKey}
            />
          </div>
        )}

        <div className="max-h-400px w-full overflow-auto" ref={parentRef}>
          <div
            className="relative w-full sm:min-w-420px"
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
            }}
          >
            {rowVirtualizer.getVirtualItems().map(virtualItem => {
              const item = filtered[virtualItem.index];
              return (
                <button
                  className={cn(
                    'EpsgButton absolute left-0 top-0 w-full cursor-pointer overflow-hidden',
                    'rounded-1 p-2 uppercase text-neutral-100 typo-text-s hover:bg-neutral-100 hover:text-neutral-800',
                  )}
                  key={virtualItem.key}
                  onClick={() => onItemClick(item)}
                  style={{
                    transform: `translateY(${virtualItem.start}px)`,
                  }}
                >
                  <div className="overflow-auto whitespace-nowrap">{item.label}</div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};
